import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, IndianRupee, Calendar,
  ShieldCheck, ListChecks, Bell, TrendingUp, AlertTriangle,
} from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton, SkeletonCard } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const moneyFmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function StatTile({ icon: Icon, label, value, sub, accent = "blue" }) {
  const cls = {
    blue: "text-blue-600 bg-blue-50",
    amber: "text-amber-700 bg-amber-50",
    emerald: "text-emerald-700 bg-emerald-50",
    rose: "text-rose-700 bg-rose-50",
    slate: "text-slate-700 bg-slate-50",
  }[accent];
  return (
    <Card>
      <CardContent className="py-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cls}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
          <p className="text-xl font-semibold text-slate-900 truncate">{value}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    v2Api.dashboard()
      .then(setStats)
      .catch((err) => setError(err?.response?.data?.detail ?? "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }
  if (error) return <p className="p-8 text-rose-600">{error}</p>;
  if (!stats) return null;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-blue-600" /> {stats.workspace_name} Dashboard
        </h1>
        <p className="text-sm text-slate-500">
          Workspace KPIs at a glance — open bids, deadlines, approvals queue,
          win-rate baseline.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={Briefcase} label="Visible projects"
          value={stats.visible_project_count}
          sub={`${stats.open_project_count} open`}
          accent="blue" />
        <StatTile icon={IndianRupee} label="Pipeline value"
          value={moneyFmt(stats.total_estimated_value_inr)}
          sub="across all visible projects"
          accent="emerald" />
        <StatTile icon={Calendar} label="Deadlines (30d)"
          value={stats.upcoming_deadline_count}
          sub="submission due in next 30 days"
          accent={stats.upcoming_deadline_count > 0 ? "amber" : "slate"} />
        <StatTile icon={ShieldCheck} label="My approvals"
          value={stats.pending_approval_steps}
          sub="awaiting your decision"
          accent={stats.pending_approval_steps > 0 ? "amber" : "slate"} />
        <StatTile icon={ListChecks} label="My checklist items"
          value={stats.pending_checklist_items}
          sub="pending or in-progress"
          accent="blue" />
        <StatTile icon={Bell} label="Unread alerts"
          value={stats.unread_notifications}
          sub="notifications inbox"
          accent={stats.unread_notifications > 0 ? "rose" : "slate"} />
        <StatTile icon={TrendingUp} label="Win rate (historical)"
          value={`${stats.win_rate_pct}%`}
          sub={`${stats.historical_won}W / ${stats.historical_lost}L · ${stats.historical_total} total`}
          accent={stats.win_rate_pct >= 50 ? "emerald" : "amber"} />
        <StatTile icon={AlertTriangle} label="High-priority projects"
          value={stats.high_priority_projects?.length || 0}
          sub="critical + high importance"
          accent="rose" />
      </div>

      {stats.deadlines_next_30_days?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-600" /> Upcoming deadlines
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1.5">
            {stats.deadlines_next_30_days.map((d) => (
              <button key={d.project_id} type="button"
                onClick={() => navigate(`/v2/projects/${d.project_id}`)}
                className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-slate-400">{d.project_code}</p>
                  <p className="font-medium text-slate-800 truncate">{d.project_name}</p>
                </div>
                <Badge className={d.days_left < 7 ? "bg-rose-100 text-rose-700" : d.days_left < 14 ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-700"}>
                  {d.days_left} days · {d.deadline}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {stats.high_priority_projects?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" /> High-priority projects
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1.5">
            {stats.high_priority_projects.map((p) => (
              <button key={p.project_id} type="button"
                onClick={() => navigate(`/v2/projects/${p.project_id}`)}
                className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-slate-400">{p.code}</p>
                  <p className="font-medium text-slate-800 truncate">{p.name}</p>
                  {p.client && <p className="text-[10px] text-slate-500">{p.client}</p>}
                </div>
                <Badge className={p.importance === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}>
                  {p.importance}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
